import React, { useEffect, useState } from "react"
import TagManager from "react-gtm-module"
import { FaArrowRight, FaCheck } from "react-icons/fa"
import { IoMdInformationCircleOutline } from "react-icons/io"

import { useRecoilValue } from "recoil"

import { userState } from "@fuse/recoil/signup"
import { stripeUrlState } from "@fuse/recoil/stripe"

import { ELogGTMEvent } from "@firebaseConfig/analyticsUtil"

import StripeConnectionModal from "../stripeConnectionModal"

interface NextStepsModalProps {
  isOpen: boolean
  onClose: () => void
  onCompleteProfile: () => void
  onConnectToStripe: () => Promise<void>
  onClickAddTickets: () => void
  saveToLocal?: () => void
}

const NextStepsModal: React.FC<NextStepsModalProps> = ({
  isOpen,
  onClose,
  onCompleteProfile,
  onConnectToStripe,
  onClickAddTickets,
  saveToLocal = () => {},
}) => {
  const user = useRecoilValue(userState)
  const [isProfileComplete, setIsProfileComplete] = useState(false)

  useEffect(() => {
    setIsProfileComplete((prev) => {
      // Check individual field states
      const hasEmail = !!user?.email
      const hasName = !!(user?.firstName || user?.name)
      const hasPhone = !!(user?.phoneNumber || user?.phone)
      const isEmailVerified = !!user?.emailVerified

      // Calculate if profile is complete
      const isComplete = hasEmail && hasName && hasPhone && isEmailVerified

      return isComplete
    })
  }, [
    user?.email,
    user?.firstName,
    user?.name,
    user?.dateOfBirth,
    user?.phoneNumber,
    user?.phone,
    user?.emailVerified,
  ])
  const isStripeConnected = !!user?.stripeAccountId
  const [showStripeConnectionModal, setShowStripeConnectionModal] =
    useState(false)
  if (!isOpen) return null

  // Track complete profile click
  const handleCompleteProfileClick = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: ELogGTMEvent.BUTTON_CLICK,
        button_name: "complete_profile",
        page_name: "NextStepsModal",
        user_id: user?.id,
        user_is_logged_in: user?.isLoggedIn || false,
        is_profile_complete: isProfileComplete,
        stripe_connected: isStripeConnected,
        user_email_verified: user?.emailVerified || false,
        modal_source: "create_event_page",
      },
    })
    onCompleteProfile()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
      onClick={onClose}
      style={{ pointerEvents: "auto" }}
    >
      <div
        className="w-full md:max-w-md bg-[#252525B2] backdrop-blur-xl rounded-t-[32px] rounded-b-none p-8 pt-6 flex flex-col items-center shadow-2xl animate-slideUp"
        style={{
          borderTopLeftRadius: 32,
          borderTopRightRadius: 32,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle for mobile look */}
        <div className="w-16 h-2 rounded-full bg-white/20 opacity-60 mb-4 mt-1" />
        <h2 className="text-white text-2xl font-bold text-center mb-2">
          Next steps
        </h2>
        <p className="text-white text-center mb-6">
          Follow these steps to go live and start selling tickets.
        </p>
        <div className="flex flex-col gap-4 w-full">
          {/* Step 1 */}
          <div
            className={`flex items-center justify-between w-full hover:opacity-80 transition ${
              !isProfileComplete ? "cursor-pointer" : "cursor-not-allowed"
            }`}
            onClick={!isProfileComplete ? handleCompleteProfileClick : () => {}}
          >
            <div className="flex items-center gap-4">
              {isProfileComplete ? (
                <div className="aspect-square bg-white/20 p-2 rounded-full flex items-center justify-center text-white">
                  <FaCheck color="white" size={16} />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-lg">
                  1
                </div>
              )}
              <span
                className={`text-white font-semibold text-base ${
                  isProfileComplete ? "line-through text-[#7F7F7F66]" : ""
                }`}
              >
                Complete your profile
              </span>
            </div>
            {!isProfileComplete ? (
              <button className="aspect-square p-2 rounded-xl bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition">
                <FaArrowRight color="white" size={20} />
              </button>
            ) : null}
          </div>
          {/* Step 2 */}
          <div
            className={`flex items-center justify-between w-full hover:opacity-80 transition ${
              !isStripeConnected && isProfileComplete
                ? "cursor-pointer"
                : "cursor-not-allowed"
            }`}
            onClick={
              !isStripeConnected && isProfileComplete
                ? () => {
                    // Track Stripe connection click
                    TagManager.dataLayer({
                      dataLayer: {
                        event: ELogGTMEvent.BUTTON_CLICK,
                        button_name: "connect_stripe",
                        page_name: "NextStepsModal",
                        user_id: user?.id,
                        user_is_logged_in: user?.isLoggedIn || false,
                        is_profile_complete: isProfileComplete,
                        stripe_connected: isStripeConnected,
                        modal_source: "create_event_page",
                      },
                    })
                    saveToLocal()
                    setShowStripeConnectionModal(true)
                  }
                : () => {}
            }
          >
            <div className="flex items-center gap-4">
              {isStripeConnected ? (
                <div className="aspect-square bg-white/20 p-2 rounded-full flex items-center justify-center text-white">
                  <FaCheck color="white" size={16} />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-lg">
                  2
                </div>
              )}
              <span
                className={`text-white font-semibold text-base ${
                  isStripeConnected ? "line-through text-[#7F7F7F66]" : ""
                }`}
              >
                Connect to Stripe
              </span>
            </div>
            {isProfileComplete && !isStripeConnected && (
              <button className="aspect-square p-2 rounded-xl bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition">
                <FaArrowRight color="white" size={20} />
              </button>
            )}
          </div>
          {/* Step 3 */}
          <div
            className="flex items-center justify-between w-full cursor-pointer hover:opacity-80 transition"
            onClick={() => {
              // Track add tickets click
              TagManager.dataLayer({
                dataLayer: {
                  event: ELogGTMEvent.BUTTON_CLICK,
                  button_name: "add_tickets_from_modal",
                  page_name: "NextStepsModal",
                  user_id: user?.id,
                  user_is_logged_in: user?.isLoggedIn || false,
                  is_profile_complete: isProfileComplete,
                  stripe_connected: isStripeConnected,
                  modal_source: "create_event_page",
                },
              })
              onClickAddTickets()
            }}
          >
            <div className="flex  gap-4">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-lg">
                3
              </div>
              <div className="flex flex-col ">
                <span className="text-white font-semibold text-base text-left">
                  Add tickets
                </span>
                <span className="text-white/35 text-sm">
                  Tickets can only be created before going live.
                </span>
              </div>
            </div>
            {isProfileComplete && isStripeConnected && (
              <button className="aspect-square p-2 rounded-xl bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition">
                <FaArrowRight color="white" size={20} />
              </button>
            )}
          </div>
        </div>
      </div>
      <style>{`
        .animate-slideUp {
          animation: slideUpSheet 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes slideUpSheet {
          from {
            transform: translateY(100%);
            opacity: 0.7;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
      <StripeConnectionModal
        isOpen={showStripeConnectionModal}
        onClose={() => setShowStripeConnectionModal(false)}
        onConnectToStripe={onConnectToStripe}
      />
    </div>
  )
}

export default NextStepsModal
